import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Bell, Search, Sun, Moon, Command, ChevronRight, Home, Menu } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const PATH_LABELS = {
  "": "Dashboard",
  dashboard: "Dashboard",
  profile: "Profile",
  resume: "Resume AI",
  chat: "Career Advisor",
  salary: "Salary Intel",
  roadmap: "Roadmap",
  jobs: "Jobs",
  network: "Network",
  interview: "Interview",
  negotiation: "Negotiation",
  financial: "Financial",
  executive: "Executive",
  insights: "Insights",
  notifications: "Notifications",
  certifications: "Certifications",
  achievements: "Achievements",
  referrals: "Referrals",
  admin: "Admin",
  settings: "Settings",
  pricing: "Pricing",
};

export default function Header({ onOpenCommand, onOpenSidebarMobile }) {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    const load = () =>
      api.get("/notifications").then((r) => setNotifCount(r.data.filter((n) => !n.read).length)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  // Breadcrumbs derived from URL
  const parts = location.pathname.replace(/^\/app\/?/, "").split("/").filter(Boolean);
  const crumbs = parts.length === 0 ? [] : parts;

  return (
    <header
      className="sticky top-0 z-30 h-14 glass-surface flex items-center px-4 md:px-6 gap-3"
      data-testid="app-header"
    >
      {/* Mobile menu */}
      <button
        onClick={onOpenSidebarMobile}
        className="md:hidden w-8 h-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        aria-label="Open menu"
        data-testid="header-mobile-menu"
      >
        <Menu size={16} />
      </button>

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="hidden md:flex items-center gap-1.5 text-sm min-w-0" data-testid="breadcrumbs">
        <Link
          to="/app"
          className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          data-testid="breadcrumb-home"
        >
          <Home size={13} />
        </Link>
        {crumbs.map((slug, i) => (
          <div key={slug + i} className="flex items-center gap-1.5">
            <ChevronRight size={12} className="text-muted-foreground/50" />
            <span
              className={`font-medium truncate ${
                i === crumbs.length - 1 ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {PATH_LABELS[slug] || slug.charAt(0).toUpperCase() + slug.slice(1)}
            </span>
          </div>
        ))}
      </nav>

      {/* Global search trigger */}
      <button
        onClick={onOpenCommand}
        data-testid="header-search"
        className="ml-auto flex items-center gap-2 px-3 h-9 rounded-lg border border-border bg-background/70 text-muted-foreground text-sm hover:bg-accent hover:text-foreground transition-colors min-w-0 max-w-md md:min-w-[280px]"
        aria-label="Search (Cmd+K)"
      >
        <Search size={14} />
        <span className="hidden md:inline truncate">Search or jump to…</span>
        <span className="md:hidden">Search</span>
        <kbd className="hidden md:inline-flex ml-auto items-center gap-1 rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
          <Command size={10} /> K
        </kbd>
      </button>

      {/* Notifications */}
      <Link
        to="/app/notifications"
        data-testid="header-notifications"
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        aria-label="Notifications"
      >
        <Bell size={16} strokeWidth={1.75} />
        {notifCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            data-testid="notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold font-mono flex items-center justify-center"
          >
            {notifCount > 9 ? "9+" : notifCount}
          </motion.span>
        )}
      </Link>

      {/* Theme toggle */}
      <button
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        data-testid="header-theme-toggle"
        className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
      </button>
    </header>
  );
}
